import { lockedStampValues, lockedValueCantPrint, signerCanFixPdfError } from "./pdfErrorMessage";
import { checkBatch } from "./signAllBatch";
import { logFailure } from "./signAllRun";

// ENG-1865: the sign-all page's steps. Their I/O is passed in, so each one is tested without a browser or a server.

export const secretHeader = (secret) => ({ "sign-secret-id": secret });

const gone = (error) => error?.response?.status === 403 || error?.response?.status === 404;

const lockedValuesOf = (agreement) => lockedStampValues({ isCpPage: true, cpFields: agreement.cpFields });

// The secrets are credentials: take them out of the address bar and history first, keeping the router's own history state.
export function clearHandoffHash(location, history) {
  if (location.hash) history.replaceState(history.state, "", location.pathname + location.search);
}

export async function loadLink(get, baseUrl, secret) {
  try {
    const response = await get(`${baseUrl}/get_template_sign_details?sign_secret_id=${secret}`, {
      headers: secretHeader(secret),
    });
    return response?.data?.data?.signLink;
  } catch (error) {
    // Gone or revoked reads as "no longer available", same as any other agreement that vanished mid-batch.
    if (gone(error)) return null;
    throw error;
  }
}

export async function loadBatch({ enabled, secrets, load, mode }) {
  if (!enabled) return { refusal: "disabled", agreements: [], alreadySigned: [], unavailable: [] };
  const loaded = [];
  for (const secret of secrets) {
    try {
      const signLink = await load(secret);
      loaded.push(signLink ? { secret, signLink } : { secret, error: true });
    } catch {
      loaded.push({ secret, error: true });
    }
  }
  return checkBatch(loaded, mode);
}

// Don't cache a transient fetch failure for the page's lifetime: only a successful body goes in.
export async function fetchPdfOnce(cache, fetchImpl, key, url) {
  if (cache.has(key)) return cache.get(key);
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`Couldn't fetch the PDF (${response.status})`);
  const bytes = await response.arrayBuffer();
  cache.set(key, bytes);
  return bytes;
}

// Builds every PDF before any upload: a character the signer typed stops the batch, marking even the open row; any other failure holds only that agreement.
export async function signAfterPreflight(list, { build, onBuilt, onBuildFailed, onHeld, onBlocked, run }) {
  const held = new Set();
  let stopped = false;
  for (const agreement of list) {
    try {
      await build(agreement);
      onBuilt(agreement);
    } catch (error) {
      logFailure("build", agreement.label, error);
      if (signerCanFixPdfError(error, lockedValuesOf(agreement))) {
        onBuildFailed(agreement, error);
        stopped = true;
      } else {
        onHeld(agreement, error);
        held.add(agreement);
      }
    }
  }
  if (stopped) {
    onBlocked();
    return false;
  }
  const rest = list.filter((agreement) => !held.has(agreement));
  // With every agreement held there's no run; the page shows the held rows as it does any batch with nothing left to sign.
  if (rest.length === 0) return false;
  await run(rest);
  return true;
}

// A value on file the font can't print fails every build of that agreement, its preview's first, so it's held there; any other preview failure stays on its row, and reopening retries it.
export function sortPreviewFailure(agreement, error, { onHeld, onBuildFailed }) {
  if (lockedValueCantPrint(error, lockedValuesOf(agreement))) {
    logFailure("build", agreement.label, error);
    onHeld(agreement, error);
  } else {
    onBuildFailed(agreement, error);
  }
}

// A link revoked after the page loaded mustn't block the signature, so a 403 or 404 moves on to the next agreement's link.
export async function uploadSignature(secrets, upload) {
  let lastError = new Error("no link to upload the signature with");
  for (const secret of secrets) {
    try {
      return await upload(secret);
    } catch (error) {
      if (!gone(error)) throw error;
      lastError = error;
    }
  }
  throw lastError;
}

// date_signed records the day of signing, so a batch opened before midnight is prepared again to print that day.
export function refreshForToday(prepared, today, prepare) {
  if (prepared.date === today) return prepared;
  return { eligible: prepared.eligible, date: today, agreements: prepare(prepared.eligible, today) };
}
